import React, { useRef, useState } from "react";
import { TransportData, TripTransport } from "@/types/trip";
import { motion } from "framer-motion";
import { DateTime } from "luxon";
import { ClockIcon } from "@heroicons/react/24/outline";

interface FlightInfoModalProps {
  flight: TripTransport;
  isOpen: boolean;
  handleClose: () => void;
  transportData: TransportData;
  toraRecommendation?: boolean;
}

const calculateDuration = (
  departureTime: string,
  arrivalTime: string,
  timezone1: string,
  timezone2: string
) => {
  const departure = DateTime.fromISO(departureTime, {
    zone: timezone1,
  });
  const arrival = DateTime.fromISO(arrivalTime, {
    zone: timezone2,
  });

  // Convert both to UTC before calculating duration
  const durationMilliseconds = arrival
    .toUTC()
    .diff(departure.toUTC())
    .as("milliseconds");
  const durationMinutes = Math.round(durationMilliseconds / (1000 * 60));
  return durationMinutes;
};

const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
};

const FlightInfoModal: React.FC<FlightInfoModalProps> = ({
  flight,
  isOpen,
  handleClose,
  transportData,
  toraRecommendation = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const prevTransportDataRef = useRef(transportData);

  if (!isOpen) return null;

  if (!flight.legs || flight.legs.length === 0) return null;

  const firstLeg = flight.legs[0];
  const lastLeg = flight.legs[flight.legs.length - 1];

  if (!firstLeg || !lastLeg) return null;

  const totalDuration = calculateDuration(
    firstLeg.departure.time.utc,
    lastLeg.arrival.time.utc,
    firstLeg.departure.location.timezone,
    lastLeg.arrival.location.timezone
  );

  const numStops = flight.legs.length - 1;

  return (
    <motion.div
      className="absolute inset-0 z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
    >
      <motion.div
        className="absolute inset-0 bg-white/20 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
      />
      <motion.div
        className={`w-full h-full ${
          toraRecommendation ? "p-0" : "p-3"
        } flex flex-col gap-1`}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{
          duration: 0.2,
          ease: "easeInOut",
        }}
      >
        <div
          ref={modalRef}
          className="h-[calc(100vh-1.4rem)] bg-white/90 backdrop-blur-xl rounded-lg px-4 pb-2 flex flex-col border border-neutral-200"
        >
          <div className="flex flex-col items-center justify-start relative">
            <div className="w-full overflow-y-scroll no-scrollbar h-[calc(100vh-2.5rem)] rounded-lg mt-2 pb-[5rem]">
              <div className="flex flex-row justify-between items-start pt-3">
                <div className="flex-1 max-w-[calc(100%-5rem)] sticky top-0 z-10 mb-2">
                  <div className="flex flex-col items-start justify-between">
                    <h2 className="text-lg font-medium text-neutral-800 pt-1 mb-0.5 line-clamp-2">
                      {`${firstLeg.departure.location.name} to ${lastLeg.arrival.location.name}`}
                    </h2>
                    <div className="flex flex-row items-center space-x-2 mt-2">
                      <div className="flex flex-row items-center space-x-2 bg-neutral-100 rounded-full px-4 py-1.5">
                        <i className="far fa-clock text-neutral-700 text-xs"></i>
                        <p className="text-neutral-700 font-light text-xs leading-relaxed">
                          {formatDuration(totalDuration)}
                        </p>
                      </div>
                      <div className="flex flex-row items-center space-x-2 bg-neutral-100 rounded-full px-4 py-1.5">
                        <i className="far fa-plane text-neutral-700 text-xs"></i>
                        <p className="text-neutral-700 font-light text-xs leading-relaxed">
                          {numStops === 0
                            ? "Non-stop"
                            : `${numStops} stop${numStops > 1 ? "s" : ""}`}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex-none flex flex-col items-center justify-start gap-2 ml-4">
                  <button
                    onClick={handleClose}
                    className="text-xs text-neutral-600 hover:text-neutral-800 border border-neutral-200 hover:border-neutral-600 h-8 rounded-full transition-all duration-300 w-16 flex items-center justify-center gap-1"
                  >
                    <i className="fa-solid fa-xmark text-[11px]"></i>
                    Close
                  </button>
                </div>
              </div>

              <div className="mt-2 flight-route-card-container w-full">
                {flight.legs.map((leg, index) => (
                  <React.Fragment key={index}>
                    <div className="flight-route-card w-[95%] rounded-xl bg-white p-5 shadow-sm mb-4 border-[1px] relative">
                      <div className="mb-5 flex flex-row w-full items-center space-x-3.5 transform -translate-x-1">
                        <img
                          src={
                            leg.provider.logo ||
                            `https://images.kiwi.com/airlines/128x128/${leg.provider.code}.png?default=airline.png`
                          }
                          className="route-airline-logo h-11 w-11 rounded-lg shadow-sm"
                          alt="Airline Logo"
                        />
                        <div className="flex flex-col">
                          <h1 className="route-airline-name text-[13px] font-medium text-neutral-700">
                            {leg.provider.name}
                          </h1>
                          <div className="flex flex-row items-center">
                            <h1 className="route-airline-number text-[13px] font-light text-neutral-500">
                              {leg.reference || `${leg.provider.code}`}
                            </h1>
                            <div className="route-divider-circle ml-2 mr-2 h-1.5 w-1.5 rounded-full bg-neutral-300"></div>
                            <p className="route-airline-duration text-[13px] font-light text-neutral-500">
                              {formatDuration(leg.duration)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex w-full items-center space-x-8">
                        <i className="route-icon-departure fa-solid fa-plane-departure text-[13px] text-neutral-700"></i>
                        <div className="flex flex-row space-x-4">
                          <div className="flex flex-col items-start justify-between">
                            <p className="route-departure-time text-[13px] font-medium text-neutral-700">
                              {flight.type === "api" && leg.departure.time.local
                                ? formatTimeToHHMM(leg.departure.time.local)
                                : formatTimeToHHMM(leg.departure.time.utc)}
                            </p>
                            <p className="route-departure-date text-xs font-light text-neutral-500">
                              {flight.type === "api" && leg.departure.time.local
                                ? formatDateToDateMonth(
                                    leg.departure.time.local
                                  )
                                : formatDateToDateMonth(leg.departure.time.utc)}
                            </p>
                          </div>
                          <div className="flex flex-col items-start justify-between transform -translate-y-0.5">
                            <p className="route-departure-city text-[14px] font-medium text-neutral-700">
                              {leg.departure.location.name}
                            </p>
                            <div className="flex flex-row items-center space-x-1">
                              <p className="route-departure-airport text-xs font-light text-neutral-500">
                                {leg.departure.location.code}
                              </p>
                              {leg.departure.terminal && (
                                <>
                                  <p className="route-departure-airport text-[11px] font-light text-neutral-500">
                                    -
                                  </p>
                                  <p className="route-departure-airport text-[12px] font-light text-neutral-500">
                                    Terminal {leg.departure.terminal}
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 flex w-full items-center space-x-8 transform -translate-y-0.5">
                        <i className="route-icon-arrival fa-solid fa-plane-arrival text-[13px] text-neutral-700"></i>
                        <div className="flex flex-row space-x-4">
                          <div className="flex flex-col items-start justify-between">
                            <p className="route-arrival-time text-[13px] font-medium text-neutral-700">
                              {flight.type === "api" && leg.arrival.time.local
                                ? formatTimeToHHMM(leg.arrival.time.local)
                                : formatTimeToHHMM(leg.arrival.time.utc)}
                            </p>
                            <p className="route-arrival-date text-xs font-light text-neutral-500">
                              {flight.type === "api" && leg.arrival.time.local
                                ? formatDateToDateMonth(leg.arrival.time.local)
                                : formatDateToDateMonth(leg.arrival.time.utc)}
                            </p>
                          </div>
                          <div className="flex flex-col items-start justify-between">
                            <p className="route-arrival-city text-[14px] font-medium text-neutral-700">
                              {leg.arrival.location.name}
                            </p>
                            <div className="flex flex-row items-center space-x-1">
                              <p className="route-arrival-airport text-xs font-light text-neutral-500">
                                {leg.arrival.location.code}
                              </p>
                              {leg.arrival.terminal && (
                                <>
                                  <p className="route-arrival-airport text-[11px] font-light text-neutral-500">
                                    -
                                  </p>
                                  <p className="route-arrival-airport text-[12px] font-light text-neutral-500">
                                    Terminal {leg.arrival.terminal}
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {index < flight.legs.length - 1 && (
                      <div className="flex flex-row items-center justify-between w-[95%] p-3 my-4 bg-neutral-50 border border-neutral-200 rounded-xl shadow-sm">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center justify-center w-8 h-8 bg-neutral-200 rounded-full">
                            <ClockIcon className="w-5 h-5 text-neutral-500" />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-neutral-800">
                              Layover in {leg.arrival.location.name}
                            </span>
                            {(() => {
                              const nextLeg = flight.legs[index + 1];
                              if (!nextLeg) return null;
                              const layoverDuration = calculateDuration(
                                leg.arrival.time.utc,
                                nextLeg.departure.time.utc,
                                leg.arrival.location.timezone,
                                nextLeg.departure.location.timezone
                              );
                              return (
                                <span className="text-xs text-neutral-500 font-light">
                                  {formatDuration(layoverDuration)}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                        {(() => {
                          const nextLeg = flight.legs[index + 1];
                          if (!nextLeg) return null;
                          const layoverDuration = calculateDuration(
                            leg.arrival.time.utc,
                            nextLeg.departure.time.utc,
                            leg.arrival.location.timezone,
                            nextLeg.departure.location.timezone
                          );
                          return (
                            layoverDuration > 240 && (
                              <div className="flex items-center space-x-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
                                <i className="fa-solid fa-triangle-exclamation text-red-500 text-xs"></i>
                                <span className="text-xs font-medium text-red-600">
                                  Long Layover
                                </span>
                              </div>
                            )
                          );
                        })()}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

function formatTimeToHHMM(isoString: string): string {
  const date = new Date(isoString);
  const hours = date.getUTCHours().toString().padStart(2, "0");
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
}

function formatDateToDateMonth(isoString: string): string {
  const date = new Date(isoString);
  const day = date.getUTCDate().toString();
  const month = date.toLocaleString("en-US", {
    month: "long",
    timeZone: "UTC",
  });
  return `${day} ${month}`;
}

export default FlightInfoModal;
